import React, { useState, useEffect, memo, Suspense } from 'react';
import { TabsContent } from '../../components/ui/tabs';

/**
 * LazyTabContent - A wrapper that delays rendering tab content until the tab is active.
 * This significantly improves initial load performance by not rendering all 20+ tabs at once.
 * 
 * Features:
 * - Only renders children when tab becomes active for the first time
 * - Shows loading skeleton briefly while content is preparing
 * - Keeps content mounted after first render (for state preservation)
 * - Memoized to prevent unnecessary re-renders
 */
const LazyTabContent = memo(({ value, activeTab, children, className, ...props }) => {
  const [hasBeenActive, setHasBeenActive] = useState(false);
  
  const isActive = activeTab === value;
  
  useEffect(() => {
    if (isActive && !hasBeenActive) {
      setHasBeenActive(true);
    }
  }, [isActive, hasBeenActive]);

  // Don't render anything until the tab has been activated at least once
  if (!hasBeenActive) {
    return (
      <TabsContent value={value} className={className} {...props}>
        {/* Empty placeholder - tab content will render when clicked */}
      </TabsContent>
    );
  }

  // Tab has been activated - render the actual content
  return (
    <TabsContent value={value} className={className} {...props}>
      <Suspense fallback={<p data-testid={`section-loading-${value}`} role="status" className="p-6 text-sm text-slate-500">Loading…</p>}>{children}</Suspense>
    </TabsContent>
  );
});

LazyTabContent.displayName = 'LazyTabContent';

export default LazyTabContent;
